import React, { useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CalendarCog, PlayCircle, CheckCircle2, AlertTriangle,
} from "lucide-react";

/**
 * Assistant Calendar Repair (Iter-388).
 *
 * Owner-only sibling to Commission Repair Tools. Fires the
 * `/appointments/repair-assistant-snapshots` endpoint so legacy
 * appointments pick up a service's current `requires_assistant` +
 * `processing_time_min` without needing an operator to re-save every
 * historical tile. Preview is non-destructive; Apply writes.
 *
 * Hidden entirely for non-owner roles.
 */
export default function AssistantRepairTools() {
  const { staff } = useAuth();
  const amOwner = Boolean(
    staff && (staff.is_owner || staff.role_detail?.is_owner),
  );
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!amOwner) return null;

  const runPreview = async () => {
    setPreviewing(true);
    setApplied(null);
    try {
      const r = await staffApi.post(
        "/appointments/repair-assistant-snapshots",
        null,
        { params: { dry_run: true } },
      );
      setPreview(r.data);
      if (r.data.patched === 0) {
        toast.success("No drift found — every appointment is already in sync.");
      } else {
        toast.success(
          `${r.data.patched} appointment${r.data.patched === 1 ? "" : "s"} would be patched.`,
        );
      }
    } catch (err) {
      setPreview(null);
      toast.error(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const applyRepair = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const r = await staffApi.post(
        "/appointments/repair-assistant-snapshots",
        null,
        { params: { dry_run: false } },
      );
      setApplied(r.data);
      toast.success(
        `Repair applied: ${r.data.patched} appointment${r.data.patched === 1 ? "" : "s"} patched.`,
      );
      // Clear preview so a second Apply requires a fresh preview — same
      // guard as Commission Repair Tools to avoid accidental repeats.
      setPreview(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  const sample = preview?.patched_sample || [];

  return (
    <Card className="rounded-lg" data-testid="assistant-repair-tools">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-sky-50 border border-sky-200 p-2">
            <CalendarCog className="w-5 h-5 text-sky-700" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Assistant Calendar Repair</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Syncs <strong>requires_assistant</strong> and{" "}
              <strong>processing_time_min</strong> from each service onto its
              appointments. Fixes legacy tiles that still block the assistant
              for the full treatment duration or hide the "Assign Assistant"
              banner. Preview is safe to run anytime.
            </p>
          </div>
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
            Owner only
          </Badge>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={runPreview}
            disabled={previewing || applying}
            data-testid="assist-repair-preview-btn"
          >
            <PlayCircle className="w-4 h-4 mr-1" />
            {previewing ? "Previewing…" : "Preview Repair"}
          </Button>
          {preview && preview.patched > 0 && (
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={applying}
              data-testid="assist-repair-apply-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              {applying ? "Applying…" : "Apply Repair"}
            </Button>
          )}
        </div>

        {preview && (
          <div className="space-y-3" data-testid="assist-repair-preview-result">
            <div className="flex flex-wrap gap-4 text-sm">
              <span>
                <strong>{preview.scanned}</strong> appointment{preview.scanned === 1 ? "" : "s"} scanned
              </span>
              <span className="text-amber-800">
                <strong>{preview.patched}</strong> would be patched
              </span>
              {preview.skipped_no_service > 0 && (
                <span className="text-muted-foreground">
                  {preview.skipped_no_service} skipped (missing service)
                </span>
              )}
            </div>
            {preview.patched === 0 ? (
              <p className="text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Every appointment already matches its service — nothing to repair.
              </p>
            ) : (
              <div className="rounded-md border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Requires Assistant</TableHead>
                      <TableHead className="text-right">Processing (min)</TableHead>
                      <TableHead className="text-right">Service time (min)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sample.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`assist-repair-diff-row-${row.id}`}
                      >
                        <TableCell className="text-sm">
                          {row.customer_name || "—"}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <span className="text-muted-foreground">
                            {String(row.before.requires_assistant)}
                          </span>
                          {" → "}
                          <span className="font-medium">
                            {String(row.after.requires_assistant)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <span className="text-muted-foreground">
                            {row.before.processing_time_min}
                          </span>
                          {" → "}
                          <span className="font-medium">
                            {row.after.processing_time_min}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          <span className="text-muted-foreground">
                            {row.before.service_time_min}
                          </span>
                          {" → "}
                          <span className="font-medium">
                            {row.after.service_time_min}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.patched > sample.length && (
                  <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                    Showing {sample.length} of {preview.patched} affected rows.
                    Apply Repair will patch all of them.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {applied && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
            data-testid="assist-repair-apply-result"
          >
            <div className="font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Repair applied
            </div>
            <div className="mt-1 text-emerald-800">
              {applied.patched} appointment{applied.patched === 1 ? "" : "s"} patched
              {" · "}
              {applied.scanned} scanned
              {applied.skipped_no_service
                ? ` · ${applied.skipped_no_service} skipped (missing service)`
                : ""}
            </div>
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="assist-repair-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Apply assistant repair?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This patches {preview?.patched || 0} appointment
                {preview?.patched === 1 ? "" : "s"} with the current
                requires_assistant / processing_time_min values from their
                linked service. New edits already auto-sync on save, so
                this is a one-and-done backfill for legacy rows.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="assist-repair-confirm-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={applyRepair}
                data-testid="assist-repair-confirm-apply"
              >
                Apply repair now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
